import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Admin.css';

const ROLES = [
  { value: 'owner', label: 'Owner' },
  { value: 'co-owner', label: 'Co-owner' },
  { value: 'gm', label: 'GM' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'frontdesk', label: 'Front Desk' },
  { value: 'marketing', label: 'Marketing' }
];

const SECTIONS = [
  { value: 'inventory', label: 'Inventory' },
  { value: 'finance', label: 'Finance' },
  { value: 'frontdesk', label: 'Front Desk' },
  { value: 'boh', label: 'Back of House' },
  { value: 'hr', label: 'HR & Onboarding' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'technicians', label: 'Technicians' }
];

function Admin() {
  const { user, access } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', role: 'frontdesk', sections: [] });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Headers with user email for auth
  const authHeaders = {
    'X-User-Email': user?.email || ''
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/admin-access.php', { headers: authHeaders });
      const data = await res.json();
      if (data.users) {
        setUsers(data.users);
      }
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const res = await fetch('/api/admin-access.php', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...authHeaders 
        },
        body: JSON.stringify(formData)
      });
      const data = await res.json();

      if (data.success) {
        setSuccess('User added successfully');
        setShowForm(false);
        setFormData({ email: '', role: 'frontdesk', sections: [] });
        fetchUsers();
      } else {
        setError(data.error || 'Failed to add user');
      }
    } catch (err) {
      setError('Failed to add user');
    }
  };

  const handleDelete = async (email) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Remove access for ${email}?`)) return;

    try {
      const res = await fetch(`/api/admin-access.php?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      const data = await res.json();
      if (data.success) {
        fetchUsers();
      }
    } catch (err) {
      setError('Failed to remove user');
    }
  };

  const toggleSection = (section) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.includes(section)
        ? prev.sections.filter(s => s !== section)
        : [...prev.sections, section]
    }));
  };

  const handleRoleChange = (role) => {
    // Auto-select sections based on role
    const roleSections = {
      'owner': ['inventory', 'finance', 'frontdesk', 'boh', 'hr', 'marketing', 'technicians'],
      'co-owner': ['inventory', 'finance', 'frontdesk', 'boh', 'hr', 'marketing', 'technicians'],
      'gm': ['frontdesk', 'boh', 'hr', 'marketing'],
      'inventory': ['inventory', 'boh'],
      'frontdesk': ['frontdesk'],
      'marketing': ['marketing']
    };
    setFormData(prev => ({
      ...prev,
      role,
      sections: roleSections[role] || []
    }));
  };

  if (!access?.role || !['owner', 'co-owner'].includes(access.role)) {
    return (
      <div className="admin-container">
        <h1>Access Denied</h1>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>User Access Management</h1>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {error && <div className="alert-error">{error}</div>}
      {success && <div className="alert-success">{success}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <h2>Add New User</h2>
          
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })}
              placeholder="user@glossnail.bar"
              required
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <select value={formData.role} onChange={e => handleRoleChange(e.target.value)}>
              {ROLES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Sections</label>
            <div className="sections-grid">
              {SECTIONS.map(s => (
                <label key={s.value} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.sections.includes(s.value)}
                    onChange={() => toggleSection(s.value)}
                  />
                  {s.label}
                </label>
              ))}
            </div>
          </div>

          <button type="submit" className="btn-primary">Add User</button>
        </form>
      )}

      <table className="users-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Role</th>
            <th>Sections</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.email}>
              <td>{u.email}</td>
              <td><span className={`role-badge ${u.role}`}>{u.role}</span></td>
              <td>{u.sections ? JSON.parse(u.sections).join(', ') : '-'}</td>
              <td>{u.last_login || 'Never'}</td>
              <td>
                <button className="btn-danger" onClick={() => handleDelete(u.email)}>
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Admin;